import React, { useMemo, useState, useEffect } from 'react';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatNameForDisplay } from '../../utils/nameUtils';
import Button from '../common/Button';
import UpcomingMatchModal from './UpcomingMatchModal';
import {
  findPlayerActiveCourt,
  findPlayerUpcomingSlot,
  getPlayerUpcomingBannerCopy,
  resolvePlayerBannerKind,
} from '../../utils/playerSessionBannerUtils';

const MD_UP = '(min-width: 768px)';

/** Host queue overview — neutral operational green. */
const HOST_BANNER_SURFACE =
  'border-2 border-success/40 dark:border-primary/50 border-l-4 border-l-success dark:border-l-primary ' +
  'bg-success-light dark:bg-surface-elevated ' +
  'shadow-lg ring-1 ring-success/20 dark:ring-primary/25';

/** Player status banners — colour encodes urgency (soonest = warmest). */
const PLAYER_BANNER_SURFACES = {
  /** Live on court — go now */
  onCourt:
    'border-2 border-danger/45 dark:border-danger/50 border-l-4 border-l-danger ' +
    'bg-danger-light dark:bg-surface-elevated shadow-lg ring-1 ring-danger/25',
  /** Queue slot 0 — up next */
  upNext:
    'border-2 border-warning/45 dark:border-warning/50 border-l-4 border-l-warning ' +
    'bg-warning-light dark:bg-surface-elevated shadow-lg ring-1 ring-warning/25',
  /** Queue slot 1+ — on deck soon */
  onDeck:
    'border-2 border-success/40 dark:border-success/50 border-l-4 border-l-success ' +
    'bg-success-light dark:bg-surface-elevated shadow-lg ring-1 ring-success/20',
  /** Waitlist — waiting for assignment */
  waiting:
    'border-2 border-info/40 dark:border-info/50 border-l-4 border-l-info ' +
    'bg-info-light dark:bg-surface-elevated shadow-lg ring-1 ring-info/20',
  /** Peg Board — this player picks the next game */
  toPick:
    'border-2 border-primary/50 dark:border-primary/55 border-l-4 border-l-primary ' +
    'bg-primary/10 dark:bg-surface-elevated shadow-lg ring-1 ring-primary/25',
};

function getPlayerNames(teamIds, playersById) {
  if (!teamIds?.length) return [];
  return teamIds
    .map((id) => playersById[id])
    .filter(Boolean)
    .map((player) => {
      const nameFormat = formatNameForDisplay(player.name, { maxLength: 20, useInitials: false });
      return { name: nameFormat.display, gender: player.gender };
    });
}

function MatchTeamsDisplay({ match, playersById, compact = false }) {
  if (!match) return null;

  const team1Players = getPlayerNames(match.team1, playersById);
  const team2Players = getPlayerNames(match.team2, playersById);
  const nameClass = compact
    ? 'text-sm font-bold leading-snug sm:text-base md:text-lg'
    : 'text-base font-bold leading-snug sm:text-lg md:text-xl';

  return (
    <div className="flex min-w-0 flex-col gap-2 sm:gap-3 md:flex-row md:items-baseline md:gap-6">
      <div className="flex min-w-0 flex-wrap items-baseline gap-2 gap-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-team1 whitespace-nowrap sm:text-sm">
          Team 1
        </span>
        {team1Players.length > 0 ? (
          team1Players.map((player, idx) => (
            <span
              key={idx}
              className={`${nameClass} ${getGenderTextColor(player.gender)} max-w-[10.5rem] truncate sm:max-w-[13rem] md:max-w-[16rem] lg:max-w-none`}
            >
              {player.name}
              {idx < team1Players.length - 1 ? ',' : ''}
            </span>
          ))
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </div>
      <span className="flex-shrink-0 self-center px-0.5 text-xs font-semibold uppercase tracking-wide text-text-muted md:px-1">
        vs
      </span>
      <div className="flex min-w-0 flex-wrap items-baseline gap-2 gap-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-team2 whitespace-nowrap sm:text-sm">
          Team 2
        </span>
        {team2Players.length > 0 ? (
          team2Players.map((player, idx) => (
            <span
              key={idx}
              className={`${nameClass} ${getGenderTextColor(player.gender)} max-w-[10.5rem] truncate sm:max-w-[13rem] md:max-w-[16rem] lg:max-w-none`}
            >
              {player.name}
              {idx < team2Players.length - 1 ? ',' : ''}
            </span>
          ))
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </div>
    </div>
  );
}

function BannerChrome({
  mobileBannerMinimized,
  setMobileBannerMinimized,
  minimizedTitle,
  minimizedSubtitle,
  title,
  subtitle,
  icon = '🏸',
  children,
  action = null,
  footer = null,
  surfaceClass = HOST_BANNER_SURFACE,
}) {
  return (
    <>
      <div
        className={`mb-2 md:hidden ${
          mobileBannerMinimized ? 'block' : 'hidden'
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileBannerMinimized(false)}
          className={`flex w-full items-center justify-between gap-2 rounded-lg ${surfaceClass} px-3 py-2.5 text-left shadow-md`}
          aria-expanded={false}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="text-lg leading-none" aria-hidden>
              {icon}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-text-primary">{minimizedTitle}</span>
              <span className="block truncate text-xs text-text-muted">{minimizedSubtitle}</span>
            </span>
          </span>
          <svg className="h-5 w-5 shrink-0 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div
        className={`mb-4 rounded-lg ${surfaceClass} md:!block ${
          mobileBannerMinimized ? 'hidden' : 'block'
        }`}
      >
        <div className="relative max-w-full mx-auto px-3 sm:px-4 py-4">
          <button
            type="button"
            onClick={() => setMobileBannerMinimized(true)}
            className="absolute right-2 top-2 z-10 rounded-md p-1.5 text-text-muted hover:bg-surface-subtle hover:text-text-primary md:hidden"
            aria-label="Minimise banner"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <div className="flex flex-col md:grid md:grid-cols-[auto,1fr,auto] md:items-center gap-3 md:gap-4 pr-8 md:pr-0">
            <div className="flex min-w-0 flex-col flex-shrink-0 gap-0.5">
              <div className="flex items-center space-x-2">
                <span className="text-xl" aria-hidden>
                  {icon}
                </span>
                <span className="text-sm font-semibold text-text-primary sm:text-base">{title}</span>
              </div>
              {subtitle ? (
                <span className="pl-8 text-xs text-text-muted sm:text-sm">{subtitle}</span>
              ) : null}
            </div>
            {children ? (
              <div className="flex-1 min-w-0 flex justify-center md:justify-start">{children}</div>
            ) : (
              <div className="hidden md:block" />
            )}
            {action ? (
              <div className="flex-shrink-0 flex justify-center md:justify-end">{action}</div>
            ) : null}
          </div>
          {footer}
        </div>
      </div>
    </>
  );
}

function HostUpcomingMatchBanner({
  nextAssignments,
  playersById,
  onFillUpcomingCourt,
  matchmakingValidation,
  mobileBannerMinimized,
  setMobileBannerMinimized,
  showModal,
  setShowModal,
  playersPerSide,
  showEloTooltip = true,
}) {
  const upcomingMatch = nextAssignments.length > 0 ? nextAssignments[0] : null;
  const secondUpcomingMatch = nextAssignments.length > 1 ? nextAssignments[1] : null;
  const team1Second = secondUpcomingMatch ? getPlayerNames(secondUpcomingMatch.team1, playersById) : [];
  const team2Second = secondUpcomingMatch ? getPlayerNames(secondUpcomingMatch.team2, playersById) : [];

  const handleCreateUpcomingMatch = async () => {
    if (!onFillUpcomingCourt) return;
    try {
      await onFillUpcomingCourt(0);
    } catch (error) {
      console.error('Error creating upcoming match:', error);
    }
  };

  return (
    <>
      <BannerChrome
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        minimizedTitle={
          upcomingMatch
            ? secondUpcomingMatch
              ? 'Next on court (2 queued)'
              : 'Next on court'
            : 'Upcoming match'
        }
        minimizedSubtitle="Tap to show details"
        title={upcomingMatch ? 'Next on court 1' : 'No upcoming match'}
        action={
          upcomingMatch ? (
            <Button
              onClick={() => setShowModal(true)}
              variant="primary"
              size="small"
              className="w-full md:w-auto"
            >
              {nextAssignments.length > 1 ? 'View upcoming matches' : 'View upcoming match'}
            </Button>
          ) : (
            onFillUpcomingCourt ? (
              <Button
                onClick={handleCreateUpcomingMatch}
                variant="primary"
                size="small"
                disabled={!matchmakingValidation.possible}
                title={
                  !matchmakingValidation.possible
                    ? matchmakingValidation.reason
                    : 'Create upcoming match from the queue'
                }
                className="w-full md:w-auto"
              >
                Create Upcoming Match
              </Button>
            ) : null
          )
        }
        footer={
          secondUpcomingMatch ? (
            <div className="mt-3 border-t border-success/25 pt-3 dark:border-primary/25">
              <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:gap-4">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-text-secondary sm:text-sm">
                  Next on court 2
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4 sm:gap-y-1">
                  <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-team1 whitespace-nowrap sm:text-sm">
                      T1
                    </span>
                    {team1Second.length > 0 ? (
                      team1Second.map((player, idx) => (
                        <span
                          key={idx}
                          className={`text-sm font-bold leading-snug sm:text-base md:text-lg ${getGenderTextColor(player.gender)} truncate max-w-[120px] sm:max-w-[180px]`}
                        >
                          {player.name}
                          {idx < team1Second.length - 1 ? ',' : ''}
                        </span>
                      ))
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </div>
                  <span className="hidden shrink-0 self-center text-xs font-semibold uppercase tracking-wide text-text-muted sm:inline md:self-baseline">
                    vs
                  </span>
                  <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-team2 whitespace-nowrap sm:text-sm">
                      T2
                    </span>
                    {team2Second.length > 0 ? (
                      team2Second.map((player, idx) => (
                        <span
                          key={idx}
                          className={`text-sm font-bold leading-snug sm:text-base md:text-lg ${getGenderTextColor(player.gender)} truncate max-w-[120px] sm:max-w-[180px]`}
                        >
                          {player.name}
                          {idx < team2Second.length - 1 ? ',' : ''}
                        </span>
                      ))
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null
        }
      >
        {upcomingMatch ? <MatchTeamsDisplay match={upcomingMatch} playersById={playersById} /> : null}
      </BannerChrome>

      {showModal && nextAssignments.length > 0 ? (
        <UpcomingMatchModal
          matches={nextAssignments}
          playersById={playersById}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          playersPerSide={playersPerSide}
          showEloTooltip={showEloTooltip}
        />
      ) : null}
    </>
  );
}

function PlayerSessionStatusBanner({
  viewerPlayerId,
  courtAssignments,
  nextAssignments,
  waitingList,
  playersById,
  mobileBannerMinimized,
  setMobileBannerMinimized,
  showModal,
  setShowModal,
  playersPerSide,
  showEloTooltip = true,
  pegBoardPickerId = null,
  onOpenPegBoardPick,
}) {
  const bannerKind = useMemo(
    () =>
      resolvePlayerBannerKind({
        viewerPlayerId,
        courtAssignments,
        nextAssignments,
        waitingList,
        pegBoardPickerId,
      }),
    [viewerPlayerId, courtAssignments, nextAssignments, waitingList, pegBoardPickerId]
  );

  const activeCourt = useMemo(
    () => findPlayerActiveCourt(courtAssignments, viewerPlayerId),
    [courtAssignments, viewerPlayerId]
  );
  const upcomingSlot = useMemo(
    () => findPlayerUpcomingSlot(nextAssignments, viewerPlayerId),
    [nextAssignments, viewerPlayerId]
  );

  if (bannerKind === 'hidden') return null;

  if (bannerKind === 'to_pick') {
    return (
      <BannerChrome
        surfaceClass={PLAYER_BANNER_SURFACES.toPick}
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        minimizedTitle="Your turn to pick"
        minimizedSubtitle="Pick the next game"
        title="Your turn to pick the next game"
        subtitle="You're first in the queue"
        icon="🎯"
        action={
          typeof onOpenPegBoardPick === 'function' ? (
            <Button
              onClick={onOpenPegBoardPick}
              variant="primary"
              size="small"
              className="w-full md:w-auto"
            >
              Pick game
            </Button>
          ) : null
        }
      />
    );
  }

  if (bannerKind === 'waiting') {
    return (
      <BannerChrome
        surfaceClass={PLAYER_BANNER_SURFACES.waiting}
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        minimizedTitle="Waiting to be placed on court"
        minimizedSubtitle="You'll play when a court opens"
        title="Waiting to be placed on court"
        subtitle="Hang tight — you'll be placed when a court opens"
        icon="⏳"
      />
    );
  }

  if (bannerKind === 'on_court') {
    const courtNumber = activeCourt?.match?.courtNumber ?? activeCourt?.index + 1;
    return (
      <BannerChrome
        surfaceClass={PLAYER_BANNER_SURFACES.onCourt}
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        minimizedTitle="On court"
        minimizedSubtitle={courtNumber ? `Court ${courtNumber}` : 'Your match is live'}
        title="On court"
        subtitle={courtNumber ? `Court ${courtNumber}` : 'Head to your court'}
        icon="🏸"
      >
        <MatchTeamsDisplay match={activeCourt?.match} playersById={playersById} />
      </BannerChrome>
    );
  }

  const upcomingIndex = upcomingSlot?.index ?? 0;
  const { title, subtitle } = getPlayerUpcomingBannerCopy(upcomingIndex);
  const queueLabel =
    upcomingIndex === 0 ? 'Next on court' : `Next on court ${upcomingIndex + 1}`;
  const upcomingSurface =
    upcomingIndex === 0 ? PLAYER_BANNER_SURFACES.upNext : PLAYER_BANNER_SURFACES.onDeck;

  return (
    <>
      <BannerChrome
        surfaceClass={upcomingSurface}
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        minimizedTitle={title}
        minimizedSubtitle={subtitle}
        title={title}
        subtitle={`${subtitle} · ${queueLabel}`}
        action={
          upcomingSlot?.match ? (
            <Button
              onClick={() => setShowModal(true)}
              variant="primary"
              size="small"
              className="w-full md:w-auto"
            >
              View your match
            </Button>
          ) : null
        }
      >
        <MatchTeamsDisplay match={upcomingSlot?.match} playersById={playersById} />
      </BannerChrome>

      {showModal && upcomingSlot?.match ? (
        <UpcomingMatchModal
          matches={[upcomingSlot.match]}
          playersById={playersById}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          playersPerSide={playersPerSide}
          showEloTooltip={showEloTooltip}
        />
      ) : null}
    </>
  );
}

const UpcomingMatchBanner = ({
  nextAssignments = [],
  playersById = {},
  onFillUpcomingCourt,
  matchmakingValidation = { possible: true, reason: '' },
  playersPerSide = 2,
  isHostView = true,
  viewerPlayerId = null,
  courtAssignments = [],
  waitingList = [],
  showEloTooltip = true,
  pegBoardPickerId = null,
  onOpenPegBoardPick,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [mobileBannerMinimized, setMobileBannerMinimized] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MD_UP);
    const sync = () => {
      if (mq.matches) setMobileBannerMinimized(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    setShowModal(false);
  }, [isHostView, viewerPlayerId, nextAssignments, courtAssignments, waitingList]);

  if (isHostView) {
    return (
      <HostUpcomingMatchBanner
        nextAssignments={nextAssignments}
        playersById={playersById}
        onFillUpcomingCourt={onFillUpcomingCourt}
        matchmakingValidation={matchmakingValidation}
        mobileBannerMinimized={mobileBannerMinimized}
        setMobileBannerMinimized={setMobileBannerMinimized}
        showModal={showModal}
        setShowModal={setShowModal}
        playersPerSide={playersPerSide}
        showEloTooltip={showEloTooltip}
      />
    );
  }

  return (
    <PlayerSessionStatusBanner
      viewerPlayerId={viewerPlayerId}
      courtAssignments={courtAssignments}
      nextAssignments={nextAssignments}
      waitingList={waitingList}
      playersById={playersById}
      mobileBannerMinimized={mobileBannerMinimized}
      setMobileBannerMinimized={setMobileBannerMinimized}
      showModal={showModal}
      setShowModal={setShowModal}
      playersPerSide={playersPerSide}
      showEloTooltip={showEloTooltip}
      pegBoardPickerId={pegBoardPickerId}
      onOpenPegBoardPick={onOpenPegBoardPick}
    />
  );
};

export default UpcomingMatchBanner;
